'use client';

import React, { useMemo, useState } from 'react';
import { cn } from '@/lib/cn';
import { Button } from '@/components/ui/Button';
import { BeatLoader } from '@/components/ui';
import { IconEdit, IconExpandDiagonal, IconShrinkDiagonal } from '@repo/svgs';
import { useBuilding } from '@/app/building/context/BuildingProvider';
import { useStore } from '@/providers/StoreProvider';
import { useAuthModal } from '@/hooks/useAuthModal';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { createPortal } from 'react-dom';
import { useAnalytics } from '@/hooks/useAnalytics';
import { SectionHeader } from '@/components/ui';
import { clearSubmittedReviewRedirect } from '@/app/review/submittedReviewRedirect';

export interface BuildingActionBarProps {
  /** Optional className to customize container */
  className?: string;
}

export function BuildingActionBar({ className }: BuildingActionBarProps) {
  const analytics = useAnalytics();
  const { building, isMasked } = useBuilding();
  const { openAuthFlow } = useAuthModal();
  const isLoggedIn = useStore(state => state.webAuth.isLoggedIn);
  const isUpdatingAccess = useStore(state => state.explorer.isUpdatingAccess);
  const [isExpanded, setIsExpanded] = useState(true);

  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const currentPath = useMemo(() => {
    const qs = searchParams.toString();
    return qs ? `${pathname}?${qs}` : pathname;
  }, [pathname, searchParams]);

  const handleLogin = () => {
    void openAuthFlow();
  };

  const maskedTitle = 'Unlock building insights';
  const maskedSubtitle =
    'Review your building to access tenant reviews OR purchase access to unlock insights.';

  const handleReview = ({ isBuildingReview = false }: { isBuildingReview?: boolean }) => {
    analytics.track('building_review_prompt', {
      type: isBuildingReview ? 'edit review' : 'Write review',
      buildingId: building?.slug,
      addressSlug: building?.primaryAddress?.slug,
    });
    if (isBuildingReview) {
      if (building?.slug) {
        clearSubmittedReviewRedirect(building.slug);
      }
      router.push(`/review/${building?.slug}`);
    } else {
      router.push(`/review`);
    }
  };

  const renderContent = () => {
    if (!isMasked) {
      return (
        <div className="hidden md:block">
          <div className={cn('flex gap-1 items-center justify-center px-2 py-2')}>
            <h3 className="text-heading-6 w-full text-center md:text-left text-primary">
              Review {building?.primaryAddress?.streetAddress || 'this building'}
            </h3>
          </div>
          <div className={cn('flex flex-col gap-4 items-start px-2 py-0 w-full')}>
            <p className="text-body-2 text-primary w-full">
              Reviews are anonymous and could truly help the next renter that considers this
              building.
            </p>
            <div className="flex flex-col gap-2 items-start w-full">
              <div className="flex flex-col gap-4 items-start justify-center w-full">
                <Button
                  variant="primary"
                  size="md"
                  fullWidth
                  onClick={() => handleReview({ isBuildingReview: true })}
                  iconLeft={<IconEdit className="h-4 w-4 icon-inverted" />}
                >
                  Review this building
                </Button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    const handlePurchase = () => {
      analytics.track('get_platform_access', { page: 'building' });
      router.push(`/subscription?returnTo=${encodeURIComponent(currentPath)}&source=building`);
    };

    return (
      <>
        <div className={cn('flex gap-1 items-center justify-between w-full px-2 md:px-0')}>
          <SectionHeader className="-mx-2" title={maskedTitle} />
          {isExpanded ? (
            <IconShrinkDiagonal
              className={cn('w-4 h-4 icon-primary md:hidden')}
              onClick={() => setIsExpanded(!isExpanded)}
            />
          ) : (
            <IconExpandDiagonal
              className={cn('w-4 h-4 icon-primary md:hidden')}
              onClick={() => setIsExpanded(!isExpanded)}
            />
          )}
        </div>
        <div className={cn('flex flex-col gap-4 items-start px-2 py-0 w-full')}>
          {isExpanded ? <p className="text-body-2 text-primary w-full">{maskedSubtitle}</p> : null}
          <div className="flex flex-col gap-2 items-start w-full">
            <div className="flex flex-col gap-4 items-start justify-center w-full">
              <Button
                variant="primary"
                size="md"
                fullWidth
                onClick={() => handleReview({ isBuildingReview: false })}
                iconLeft={<IconEdit className="h-4 w-4 icon-inverted" />}
              >
                Review your building
              </Button>
              <Button variant="secondary" size="md" fullWidth onClick={handlePurchase}>
                Unlock insights
              </Button>
            </div>

            {!isLoggedIn ? (
              <div className={cn('flex gap-2 items-center justify-center w-full')}>
                <p className="text-body-2 text-primary">Already have an account?</p>
                <button
                  onClick={handleLogin}
                  className={cn(
                    'flex gap-1 items-center justify-center px-0 py-2',
                    'text-body-2-high text-accent text-center cursor-pointer',
                    'hover:underline focus-visible:outline-none focus-visible:underline',
                  )}
                >
                  Log in
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </>
    );
  };

  return (
    <div
      className={cn(
        'bg-primary flex flex-col items-start w-full relative overflow-hidden',
        'shadow-md-reverse md:shadow',
        !isMasked && 'hidden md:block',
        className,
      )}
    >
      <div className={cn('flex flex-col items-start justify-center pb-4 pt-1 px-4 w-full')}>
        <div className={cn('flex flex-col items-start justify-center px-0 py-2 w-full')}>
          {renderContent()}
        </div>
      </div>

      {isUpdatingAccess &&
        createPortal(
          <div
            className={cn(
              'fixed inset-0 z-50 bg-white/50 flex flex-col gap-2 items-center justify-center',
            )}
          >
            <BeatLoader />
          </div>,
          document.body,
        )}
    </div>
  );
}
